import { ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Image, Shape } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { useCallback } from "react";

import { type AvatarGeneratedImage, AvatarGeneratedImageType } from "@/components/AvatarGeneratorModal/types";
import Trans from "@/components/Trans/Trans";
import { cn } from "@/utils";

import ImageThumbnailBlurred from "./ImageThumbnailBlurred";

interface IImageThumbnailProps {
  image: AvatarGeneratedImage;
  index: number;
  isSelected: boolean;
  isAvatarGenerating: boolean;
  isDragging: boolean;
  imageRef: (el: HTMLDivElement | null) => void;
  onEdit?: () => void;
  onDownload?: () => void;
  onCrop?: () => void;
  onDelete?: () => void;
  onImageSelect: (index: number) => void;
  setEnlargedImageIndex: (index: number) => void;
}

export const ImageThumbnail = ({
  image,
  index,
  isSelected,
  isAvatarGenerating,
  isDragging,
  imageRef,
  onEdit,
  onDownload,
  onCrop,
  onDelete,
  onImageSelect,
  setEnlargedImageIndex,
}: IImageThumbnailProps) => {
  const { t } = useTranslation();
  const onClick = useCallback(() => {
    if (isSelected) {
      setEnlargedImageIndex(index);
    } else {
      onImageSelect(index);
    }
  }, [isSelected, setEnlargedImageIndex, index, onImageSelect]);

  if (image?.isNsfw) {
    return <ImageThumbnailBlurred index={index} isDragging={isDragging} onImageSelect={onImageSelect} isSelected={isSelected} />;
  }

  return (
    <div ref={imageRef} className="flex flex-col gap-1 flex-shrink-0">
      <div
        className={cn(
          "cursor-pointer transition-all w-[154px] h-[200px] md:w-[240px] md:h-[312px] relative flex items-center justify-center rounded-md",
          isDragging ? "cursor-grabbing" : "cursor-pointer",
        )}
        onClick={onClick}
      >
        {image.generationType === AvatarGeneratedImageType.EDIT && (
          <div className="absolute top-2 right-1 bg-gray-700 text-white text-[8px] px-2 py-0.5 rounded-full">
            <Trans i18nKey="common:modal.generateAvatar.gallery.editedBadge" />
          </div>
        )}
        <Image
          src={`data:${image.type};base64,${image.base64}`}
          alt={t("common:modal.generateAvatar.imageAlt.generatedImage", { index: index + 1 })}
          className={cn(
            "max-w-full max-h-full object-contain rounded-md ml-2 mt-2",
            !isAvatarGenerating && isSelected && "outline outline-2 outline-blue-500",
          )}
        />
      </div>
      {!isAvatarGenerating && isSelected ? (
        <div className="flex gap-1 justify-center">
          {onEdit ? (
            <ButtonIcon
              icon={IconVariants.EDIT}
              size={ButtonSize.SMALL}
              shape={Shape.SQUARE}
              variant={ButtonVariant.TEXT}
              disabled={isAvatarGenerating}
              onClick={(e: { stopPropagation: () => void }) => {
                e.stopPropagation();
                onEdit();
              }}
              className="w-6 h-6"
              aria-label={t("common:modal.generateAvatar.ariaLabel.editImage")}
            />
          ) : null}
          {onDownload ? (
            <ButtonIcon
              icon={IconVariants.DOWNLOAD}
              size={ButtonSize.SMALL}
              shape={Shape.SQUARE}
              variant={ButtonVariant.TEXT}
              onClick={(e: { stopPropagation: () => void }) => {
                e.stopPropagation();
                onDownload();
              }}
              className="w-6 h-6"
              aria-label={t("common:modal.generateAvatar.ariaLabel.downloadImage")}
            />
          ) : null}
          {onCrop ? (
            <ButtonIcon
              icon={IconVariants.SQUARE}
              size={ButtonSize.SMALL}
              shape={Shape.SQUARE}
              variant={ButtonVariant.TEXT}
              onClick={(e: { stopPropagation: () => void }) => {
                e.stopPropagation();
                onCrop();
              }}
              className="w-6 h-6"
              aria-label={t("common:modal.generateAvatar.ariaLabel.cropImage")}
            />
          ) : null}
          {onDelete ? (
            <ButtonIcon
              icon={IconVariants.TRASH}
              size={ButtonSize.SMALL}
              shape={Shape.SQUARE}
              variant={ButtonVariant.TEXT}
              onClick={(e: { stopPropagation: () => void }) => {
                e.stopPropagation();
                onDelete();
              }}
              className="w-6 h-6"
              aria-label={t("common:modal.generateAvatar.ariaLabel.deleteImage")}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
